/**
 * Narration sized to a camera window, already in the session language.
 *
 * The assistant reads this text word for word. Translating it would change its
 * length and desync the camera, so English, Dutch, Persian, and Chinese are written here.
 */

import { estimateSpeechMs } from "./voiceSpeechSync";
import { currentVoiceLang, spokenNumber, VoiceLang } from "./voiceLanguage";

const LABELS: Record<VoiceLang, Record<string, string>> = {
  en: {
    "Heel Height": "heel height",
    "Heel Depth": "heel depth",
    "Heel Fill": "heel fill",
    Thickness: "thickness",
    "Arch Medial Height": "medial arch height",
    "Arch Lateral Height": "lateral arch height",
    "Arch Center Height": "centre arch height",
    "Arch Fill": "arch fill",
    "Arch Sagital Position": "sagittal position",
    "Heel Medial Wedge": "medial heel wedge",
    "Heel Lateral Wedge": "lateral heel wedge",
    "Forefoot Medial Wedge": "medial forefoot wedge",
    "Forefoot Lateral Wedge": "lateral forefoot wedge",
    Length: "length",
    Width: "width",
  },
  nl: {
    "Heel Height": "hielhoogte",
    "Heel Depth": "hieldiepte",
    "Heel Fill": "hielvulling",
    Thickness: "dikte",
    "Arch Medial Height": "mediale voetboog",
    "Arch Lateral Height": "laterale voetboog",
    "Arch Center Height": "middenvoetboog",
    "Arch Fill": "voetboogvulling",
    "Arch Sagital Position": "sagittale positie",
    "Heel Medial Wedge": "mediale hielwig",
    "Heel Lateral Wedge": "laterale hielwig",
    "Forefoot Medial Wedge": "mediale voorvoetwig",
    "Forefoot Lateral Wedge": "laterale voorvoetwig",
    Length: "lengte",
    Width: "breedte",
  },
  fa: {
    "Heel Height": "ارتفاع پاشنه",
    "Heel Depth": "عمق پاشنه",
    "Heel Fill": "پرشدگی پاشنه",
    Thickness: "ضخامت",
    "Arch Medial Height": "ارتفاع قوس داخلی",
    "Arch Lateral Height": "ارتفاع قوس خارجی",
    "Arch Center Height": "ارتفاع قوس مرکزی",
    "Arch Fill": "پرشدگی قوس",
    "Arch Sagital Position": "موقعیت ساژیتال",
    "Heel Medial Wedge": "گوه داخلی پاشنه",
    "Heel Lateral Wedge": "گوه خارجی پاشنه",
    "Forefoot Medial Wedge": "گوه داخلی پنجه",
    "Forefoot Lateral Wedge": "گوه خارجی پنجه",
    Length: "طول",
    Width: "عرض",
  },
  zh: {
    "Heel Height": "足跟高度",
    "Heel Depth": "足跟深度",
    "Heel Fill": "足跟填充",
    Thickness: "厚度",
    "Arch Medial Height": "内侧足弓高度",
    "Arch Lateral Height": "外侧足弓高度",
    "Arch Center Height": "足弓中央高度",
    "Arch Fill": "足弓填充",
    "Arch Sagital Position": "矢状位置",
    "Heel Medial Wedge": "足跟内侧楔",
    "Heel Lateral Wedge": "足跟外侧楔",
    "Forefoot Medial Wedge": "前足内侧楔",
    "Forefoot Lateral Wedge": "前足外侧楔",
    Length: "长度",
    Width: "宽度",
  },
};

const SIDE: Record<VoiceLang, { left: string; right: string }> = {
  en: { left: "left", right: "right" },
  nl: { left: "linker", right: "rechter" },
  fa: { left: "چپ", right: "راست" },
  zh: { left: "左", right: "右" },
};

const STORY: Record<VoiceLang, Record<string, string[]>> = {
  en: {
    "Heel Height": [
      "The heel seat is rising under the rearfoot as we watch.",
      "That lifts the calcaneus and shifts how the ankle loads in the shoe.",
    ],
    "Heel Depth": [
      "The heel cup is deepening around the rearfoot.",
      "A deeper cup holds the calcaneus more securely through heel strike.",
    ],
    "Heel Fill": [
      "The heel fill is redistributing under the rearfoot.",
      "That changes how much of the heel load the shell carries.",
    ],
    Thickness: [
      "The shell is changing thickness along the whole insole.",
      "That sets the overall stiffness and how much room it takes in the shoe.",
    ],
    "Arch Medial Height": [
      "The medial arch is building up along the inside border.",
      "That supports the longitudinal arch and resists pronation.",
    ],
    "Arch Lateral Height": [
      "The lateral arch is building up along the outside border.",
      "That supports the outer column and steadies a supinating foot.",
    ],
    "Arch Center Height": [
      "The centre of the arch is lifting under the midfoot.",
      "That spreads the load across the whole arch instead of one border.",
    ],
    "Arch Fill": [
      "The arch fill is redistributing under the midfoot.",
      "That tunes how firmly the arch is carried.",
    ],
    "Arch Sagital Position": [
      "The arch peak is sliding along the length of the foot.",
      "That places the highest point of the arch under the midfoot.",
    ],
    "Heel Medial Wedge": [
      "The medial heel wedge is tilting the rearfoot.",
      "That posts the heel inward and controls rearfoot eversion.",
    ],
    "Heel Lateral Wedge": [
      "The lateral heel wedge is tilting the rearfoot.",
      "That posts the heel outward and controls rearfoot inversion.",
    ],
    "Forefoot Medial Wedge": [
      "The medial forefoot wedge is tilting the front of the insole.",
      "That posts the forefoot inward at push off.",
    ],
    "Forefoot Lateral Wedge": [
      "The lateral forefoot wedge is tilting the front of the insole.",
      "That posts the forefoot outward at push off.",
    ],
    Length: [
      "The outline is stretching from heel to toe.",
      "That sets how far the insole reaches inside the shoe.",
    ],
    Width: [
      "The outline is widening across the forefoot.",
      "That sets how much the insole spreads inside the shoe.",
    ],
  },
  nl: {
    "Heel Height": [
      "De hielzitting komt onder de achtervoet omhoog terwijl we kijken.",
      "Dat tilt het hielbeen en verandert hoe de enkel in de schoen belast.",
    ],
    "Heel Depth": [
      "De hielcup wordt dieper rond de achtervoet.",
      "Een diepere cup houdt het hielbeen steviger bij hielcontact.",
    ],
    "Heel Fill": [
      "De hielvulling verdeelt zich opnieuw onder de achtervoet.",
      "Dat bepaalt hoeveel van de hiellast de schaal draagt.",
    ],
    Thickness: [
      "De schaal verandert van dikte over de hele zool.",
      "Dat zet de stijfheid en hoeveel ruimte de zool in de schoen inneemt.",
    ],
    "Arch Medial Height": [
      "De mediale voetboog bouwt op langs de binnenrand.",
      "Dat steunt de lengteboog en remt pronatie.",
    ],
    "Arch Lateral Height": [
      "De laterale voetboog bouwt op langs de buitenrand.",
      "Dat steunt de buitenste kolom en stabiliseert een supinerende voet.",
    ],
    "Arch Center Height": [
      "Het midden van de voetboog komt onder de middenvoet omhoog.",
      "Dat spreidt de last over de hele boog in plaats van één rand.",
    ],
    "Arch Fill": [
      "De voetboogvulling verdeelt zich opnieuw onder de middenvoet.",
      "Dat stemt af hoe stevig de boog gedragen wordt.",
    ],
    "Arch Sagital Position": [
      "De top van de voetboog schuift langs de lengte van de voet.",
      "Dat zet het hoogste punt van de boog onder de middenvoet.",
    ],
    "Heel Medial Wedge": [
      "De mediale hielwig kantelt de achtervoet.",
      "Dat post de hiel naar binnen en beheerst eversie.",
    ],
    "Heel Lateral Wedge": [
      "De laterale hielwig kantelt de achtervoet.",
      "Dat post de hiel naar buiten en beheerst inversie.",
    ],
    "Forefoot Medial Wedge": [
      "De mediale voorvoetwig kantelt de voorkant van de zool.",
      "Dat post de voorvoet naar binnen bij afzet.",
    ],
    "Forefoot Lateral Wedge": [
      "De laterale voorvoetwig kantelt de voorkant van de zool.",
      "Dat post de voorvoet naar buiten bij afzet.",
    ],
    Length: [
      "De omtrek rekt van hiel naar teen.",
      "Dat bepaalt hoe ver de zool in de schoen reikt.",
    ],
    Width: [
      "De omtrek wordt breder over de voorvoet.",
      "Dat bepaalt hoe breed de zool in de schoen spreidt.",
    ],
  },
  fa: {
    "Heel Height": [
      "کف پاشنه زیر قسمت عقب پا بالا می‌آید.",
      "این کار استخوان پاشنه را بالا می‌برد و بار مچ را در کفش عوض می‌کند.",
    ],
    "Heel Depth": [
      "کاسه پاشنه دور قسمت عقب پا عمیق‌تر می‌شود.",
      "کاسه عمیق‌تر پاشنه را در ضربه پاشنه محکم‌تر نگه می‌دارد.",
    ],
    "Heel Fill": [
      "پرشدگی پاشنه زیر قسمت عقب پا دوباره پخش می‌شود.",
      "این کار سهم بار پاشنه روی پوسته را عوض می‌کند.",
    ],
    Thickness: [
      "ضخامت پوسته در تمام کفی عوض می‌شود.",
      "این کار سختی کلی و فضای کفی در کفش را تعیین می‌کند.",
    ],
    "Arch Medial Height": [
      "قوس داخلی در لبه داخل پا بالا می‌آید.",
      "این کار قوس طولی را نگه می‌دارد و پرونیشن را مهار می‌کند.",
    ],
    "Arch Lateral Height": [
      "قوس خارجی در لبه بیرون پا بالا می‌آید.",
      "این کار ستون خارجی را نگه می‌دارد و پای سوپینه‌کننده را پایدار می‌کند.",
    ],
    "Arch Center Height": [
      "وسط قوس زیر میان‌پا بالا می‌آید.",
      "این کار بار را روی کل قوس پخش می‌کند نه فقط یک لبه.",
    ],
    "Arch Fill": [
      "پرشدگی قوس زیر میان‌پا دوباره پخش می‌شود.",
      "این کار سفتی تحمل قوس را تنظیم می‌کند.",
    ],
    "Arch Sagital Position": [
      "قله قوس در طول پا جابه‌جا می‌شود.",
      "این کار بلندترین نقطه قوس را زیر میان‌پا می‌گذارد.",
    ],
    "Heel Medial Wedge": [
      "گوه داخلی پاشنه قسمت عقب پا را کج می‌کند.",
      "این کار پاشنه را به داخل پست می‌کند و اورژن را کنترل می‌کند.",
    ],
    "Heel Lateral Wedge": [
      "گوه خارجی پاشنه قسمت عقب پا را کج می‌کند.",
      "این کار پاشنه را به خارج پست می‌کند و اینورژن را کنترل می‌کند.",
    ],
    "Forefoot Medial Wedge": [
      "گوه داخلی پنجه جلوی کفی را کج می‌کند.",
      "این کار پنجه را در هل دادن به داخل پست می‌کند.",
    ],
    "Forefoot Lateral Wedge": [
      "گوه خارجی پنجه جلوی کفی را کج می‌کند.",
      "این کار پنجه را در هل دادن به خارج پست می‌کند.",
    ],
    Length: [
      "خط دور کفی از پاشنه تا پنجه کشیده می‌شود.",
      "این کار می‌گوید کفی تا کجا داخل کفش می‌رسد.",
    ],
    Width: [
      "خط دور کفی در پنجه پهن‌تر می‌شود.",
      "این کار می‌گوید کفی چقدر داخل کفش پهن می‌شود.",
    ],
  },
  zh: {
    "Heel Height": [
      "足跟座正在后足下方升高。",
      "这会抬起跟骨，并改变踝关节在鞋内的受力。",
    ],
    "Heel Depth": [
      "足跟杯正在后足周围加深。",
      "更深的足跟杯在足跟着地时更能稳住跟骨。",
    ],
    "Heel Fill": [
      "足跟填充正在后足下方重新分布。",
      "这会改变壳体承担的足跟负荷比例。",
    ],
    Thickness: [
      "整个鞋垫壳体的厚度正在变化。",
      "这决定整体刚度以及在鞋内所占空间。",
    ],
    "Arch Medial Height": [
      "内侧足弓沿足内侧缘抬高。",
      "这支撑纵弓并抑制旋前。",
    ],
    "Arch Lateral Height": [
      "外侧足弓沿足外侧缘抬高。",
      "这支撑外侧柱并稳定旋后足。",
    ],
    "Arch Center Height": [
      "足弓中央正在中足下方抬起。",
      "这把负荷分散到整个足弓，而不是只压在一侧。",
    ],
    "Arch Fill": [
      "足弓填充正在中足下方重新分布。",
      "这调节足弓被支撑的紧实程度。",
    ],
    "Arch Sagital Position": [
      "足弓最高点正沿脚的长度方向移动。",
      "这会把足弓最高点放在中足下方。",
    ],
    "Heel Medial Wedge": [
      "足跟内侧楔正在倾斜后足。",
      "这把足跟向内贴合，控制后足外翻。",
    ],
    "Heel Lateral Wedge": [
      "足跟外侧楔正在倾斜后足。",
      "这把足跟向外贴合，控制后足内翻。",
    ],
    "Forefoot Medial Wedge": [
      "前足内侧楔正在倾斜鞋垫前部。",
      "这在蹬伸时把前足向内贴合。",
    ],
    "Forefoot Lateral Wedge": [
      "前足外侧楔正在倾斜鞋垫前部。",
      "这在蹬伸时把前足向外贴合。",
    ],
    Length: [
      "轮廓正从足跟拉向足趾。",
      "这决定鞋垫在鞋内延伸多远。",
    ],
    Width: [
      "轮廓正在前足处变宽。",
      "这决定鞋垫在鞋内铺开多宽。",
    ],
  },
};

const FILLERS: Record<VoiceLang, string[]> = {
  en: [
    "The mesh is rebuilding around that marker right now.",
    "Holding on this view while the surface settles.",
    "The label is open on that marker.",
    "Take a look.",
  ],
  nl: [
    "Het mesh wordt nu rond die marker opnieuw opgebouwd.",
    "Ik blijf in dit beeld terwijl het oppervlak settelt.",
    "Het label op die marker is open.",
    "Kijk even.",
  ],
  fa: [
    "مش الان دور همان نشانگر دوباره ساخته می‌شود.",
    "روی همین نما می‌مانم تا سطح آرام بگیرد.",
    "لیبل آن نشانگر باز است.",
    "نگاه کن.",
  ],
  zh: [
    "网格正在该标记周围重新生成。",
    "表面稳定时我会停在这个视角。",
    "该标记的标签已打开。",
    "请看这里。",
  ],
};

const CLOSERS: Record<VoiceLang, string[]> = {
  en: ["That value is applied.", "Coming back to the full view.", "All set."],
  nl: ["Die waarde is toegepast.", "Ik ga terug naar het volle beeld.", "Klaar."],
  fa: ["آن مقدار اعمال شد.", "برمی‌گردم به نمای کامل.", "تمام."],
  zh: ["该数值已应用。", "回到全景。", "完成。"],
};

const SHOE_STORY: Record<VoiceLang, string[]> = {
  en: [
    "The outline is resizing from heel to toe and across the forefoot.",
    "That matches the insole to the shoe you gave me.",
  ],
  nl: [
    "De omtrek past zich van hiel tot teen en over de voorvoet aan.",
    "Dat laat de zool aansluiten op de schoenmaat die je gaf.",
  ],
  fa: [
    "خط دور از پاشنه تا پنجه و در عرض پا عوض اندازه می‌شود.",
    "این کار کفی را با سایز کفشی که گفتی جور می‌کند.",
  ],
  zh: [
    "轮廓正从足跟到足趾、并在前足横向调整尺寸。",
    "这会让鞋垫匹配你给出的鞋码。",
  ],
};

export type ParamNarrationInput = {
  fieldKey: string;
  side: "left" | "right";
  spoken: string;
  windowMs: number;
  isLast: boolean;
};

function labelFor(fieldKey: string, lang: VoiceLang): string {
  return LABELS[lang][fieldKey] ?? fieldKey.toLowerCase();
}

/**
 * One short clause, because these are spoken unconditionally: packClauses only
 * budgets the extras. Two full sentences here took about six seconds, which set
 * the length of every step no matter how the camera window was tuned.
 */
function openers(fieldKey: string, side: "left" | "right", spoken: string, lang: VoiceLang): string[] {
  const label = labelFor(fieldKey, lang);
  const foot = SIDE[lang][side];
  if (lang === "nl") {
    return [`${label}, ${foot} voet, nu ${spoken}.`];
  }
  if (lang === "fa") {
    return [`${label} کفی ${foot}، الان ${spoken}.`];
  }
  if (lang === "zh") {
    return [`${foot}脚${label}，现在${spoken}。`];
  }
  return [`${label}, ${foot} foot, now ${spoken}.`];
}

function shoeOpeners(
  side: "left" | "right",
  shoeSize: string | number,
  lengthSpoken: string,
  widthSpoken: string,
  lang: VoiceLang
): string[] {
  const foot = SIDE[lang][side];
  const size = spokenNumber(Number(shoeSize), 0);
  if (lang === "nl") {
    return [`${foot} voet, EU ${size}: lengte ${lengthSpoken}, breedte ${widthSpoken}.`];
  }
  if (lang === "fa") {
    return [`کفی ${foot}، سایز اروپایی ${size}: طول ${lengthSpoken}، عرض ${widthSpoken}.`];
  }
  if (lang === "zh") {
    return [`${foot}脚，欧码${size}：长度${lengthSpoken}，宽度${widthSpoken}。`];
  }
  return [`${foot} foot, EU ${size}: length ${lengthSpoken}, width ${widthSpoken}.`];
}

export function buildParamNarration(input: ParamNarrationInput): string {
  const { fieldKey, side, spoken, windowMs, isLast } = input;
  const lang = currentVoiceLang();
  const extras = [
    ...(STORY[lang][fieldKey] ?? STORY.en[fieldKey] ?? []),
    ...FILLERS[lang],
    ...(isLast ? CLOSERS[lang] : []),
  ];
  return packClauses(openers(fieldKey, side, spoken, lang), extras, windowMs);
}

export function buildShoeSizeNarration(input: {
  side: "left" | "right";
  shoeSize: number | string;
  lengthSpoken: string;
  widthSpoken: string;
  windowMs: number;
  isLast: boolean;
}): string {
  const { side, shoeSize, lengthSpoken, widthSpoken, windowMs, isLast } = input;
  const lang = currentVoiceLang();
  const extras = [
    ...SHOE_STORY[lang],
    ...FILLERS[lang],
    ...(isLast ? CLOSERS[lang] : []),
  ];
  return packClauses(shoeOpeners(side, shoeSize, lengthSpoken, widthSpoken, lang), extras, windowMs);
}

function packClauses(clauses: string[], extras: string[], windowMs: number): string {
  const budgetMs = Math.max(0, windowMs * 0.94);
  const picked = [...clauses];
  for (const clause of extras) {
    const next = [...picked, clause].join(" ");
    if (estimateSpeechMs(next) > budgetMs) continue;
    picked.push(clause);
  }
  return picked.join(" ");
}
